import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Extend session timeout
    storageKey: 'brandscapes-auth',
    storage: window.localStorage,
    // Refresh session 5 minutes before expiry
    flowType: 'pkce',
  },
  global: {
    headers: {
      'x-client-info': 'brandscapes-app',
    },
  },
  // Add retry logic for better reliability
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Database types
export interface Profile {
  id: string;
  full_name: string | null;
  company_name: string | null;
  avatar_url: string | null;
  account_type: 'user' | 'team' | 'super_admin';
  license?: 'free' | 'commercial' | 'template_pack' | 'professional' | 'agency';
  license_entitlements?: string[];
  jvzoo_products?: string[];
  credits_balance?: number;
  role: 'user' | 'admin' | 'super_admin';
  monthly_mockup_limit: number;
  mockups_created_this_month: number;
  subscription_status: string;
  subscription_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BrandSettings {
  id: string;
  user_id: string;
  brand_name: string | null;
  tagline: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string | null;
  brand_style: string | null;
  brand_description: string | null;
  industry: string | null;
  typography_style: string | null;
  created_at: string;
  updated_at: string;
}

export interface Asset {
  id: string;
  user_id: string;
  filename: string;
  original_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  created_at: string;
  updated_at: string;
}

export interface Template {
  id: string;
  name: string;
  category: string;
  description: string | null;
  preview_url: string | null;
  is_premium: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface Mockup {
  id: string;
  user_id: string;
  template_id: string | null;
  name: string;
  template_name?: string | null;
  primary_image_id: string | null;
  secondary_image_id: string | null;
  mockup_url: string;
  thumbnail_url?: string | null;
  settings: any;
  downloads: number;
  is_favorite?: boolean;
  file_size?: number | null;
  width?: number | null;
  height?: number | null;
  created_at: string;
  updated_at: string;
  // Relations
  template?: Template;
  primary_image?: Asset;
  secondary_image?: Asset;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_updates: boolean;
  marketing_emails: boolean;
  product_news: boolean;
  created_at: string;
  updated_at: string;
}

// Agency Feature - Team Members
export interface TeamMember {
  id: string;
  agency_owner_id: string;
  email: string;
  full_name: string;
  role: 'member' | 'manager' | 'admin';
  status: 'pending' | 'active' | 'suspended';
  permissions: {
    canCreateMockups: boolean;
    canManageAssets: boolean;
    canManageClients: boolean;
    canViewAnalytics: boolean;
    mockupLimit?: number;
  };
  invited_at: string;
  joined_at: string | null;
  last_active: string | null;
  created_at: string;
  updated_at: string;
}

// Agency Feature - Clients
export interface Client {
  id: string;
  agency_owner_id: string;
  name: string;
  company_name: string | null;
  email: string;
  phone: string | null;
  website: string | null;
  industry: string | null;
  description: string | null;
  status: 'active' | 'inactive' | 'archived';
  billing_info: {
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
  assigned_team_members: string[];
  mockups_created: number;
  total_revenue: number;
  created_at: string;
  updated_at: string;
}

// Extended Mockup interface for agency features
export interface AgencyMockup extends Mockup {
  client_id: string | null;
  team_member_id: string | null;
  client?: Client;
  team_member?: TeamMember;
}
